import { Chip, Stack, Typography, useTheme } from "@mui/material";
import { MapPin } from "lucide-react";
import type { FC } from "react";
import type { ClientPropertyTypeEnum } from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { PortfolioItemIcon } from "web_myplann/Portfolio/Components/PortfolioItemIcon";
import { DashedButton } from "web_myplann/Portfolio/Components/Redesign/Detail/DashedButton";
import { DetailHeroValueBox } from "web_myplann/Portfolio/Components/Redesign/Detail/DetailHeroValueBox";

type Props = {
	title: string;
	subtype: string;
	address?: string;
	propertyType: ClientPropertyTypeEnum;
	value?: number | null;
	valueLabel: string;
	currencySign: string;
	evaluationPercent?: number | null;
	valueSource?: string;
	forInvestments?: boolean | null;
	editable?: boolean;
	onEditValue?: () => void;
	onShowHistory?: () => void;
};

export const DetailHero: FC<Props> = ({
	title,
	subtype,
	address,
	propertyType,
	value,
	valueLabel,
	currencySign,
	evaluationPercent,
	valueSource,
	forInvestments,
	editable,
	onEditValue,
	onShowHistory,
}) => {
	const theme = useTheme();
	const { t } = useResource();

	const showEditValue = Boolean(editable && onEditValue);

	return (
		<Stack
			bgcolor={theme.palette.background.paper}
			px={theme.spacingTokens.lg}
			pt={theme.spacingTokens.lg}
			pb={theme.spacingTokens.xl}
			gap={theme.spacingTokens.md}
		>
			<Stack
				direction="row"
				alignItems="flex-start"
				gap={theme.spacingTokens.md}
			>
				<PortfolioItemIcon
					propertyType={propertyType}
					size={48}
					glyphSize={theme.iconSize.lg}
				/>
				<Stack flex={1} minWidth={0}>
					<Typography variant="h3" sx={{ overflowWrap: "anywhere" }}>
						{title}
					</Typography>
					<Typography variant="body2" color={theme.palette.grey[500]}>
						{subtype}
					</Typography>
				</Stack>
				{forInvestments && (
					<Chip
						variant="tag"
						color="info"
						label={t(Resources.PortfolioDetail.Property.ForInvestments)}
					/>
				)}
			</Stack>

			{address && (
				<Stack direction="row" alignItems="center" gap={theme.spacingTokens.sm}>
					<MapPin
						size={theme.iconSize.xs}
						color={theme.palette.grey[400]}
						style={{ flexShrink: 0 }}
					/>
					<Typography variant="body2" color={theme.palette.grey[500]}>
						{address}
					</Typography>
				</Stack>
			)}

			{(value === null || value === undefined) && showEditValue && (
				<DashedButton onClick={onEditValue}>
					<Typography variant="bodyMedium" color={theme.palette.blue[600]}>
						{t(Resources.PortfolioDetail.SharedLabels.ValueDrawer.fillTitle)}
					</Typography>
				</DashedButton>
			)}

			{value !== null && value !== undefined && (
				<DetailHeroValueBox
					value={value}
					valueLabel={valueLabel}
					currencySign={currencySign}
					evaluationPercent={evaluationPercent}
					valueSource={valueSource}
					showEditValue={showEditValue}
					onEditValue={onEditValue}
					onShowHistory={onShowHistory}
				/>
			)}
		</Stack>
	);
};
